import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { getRequest, postRequest } from "../../../../hooks/axiosClient";
import {
    discardClick,
    EnableDisable,
    PlanExpiryDetails,
    publishClick,
    chargePlanApi,
} from "../apis/otherApis";
import { decode } from "../../../../helpers/utils";
import { setCurrentFloor, setFloorList, setPinCount, setPinsByCategory, setProjectData, setShowLocationPicker } from "../../../../store/slices/projectItemSlice";
import { setMapCenter } from "../../../../store/slices/mapSlice";
import { requestNavReload } from "../../../../store/slices/navigationSlice";
import { useDispatch } from "react-redux";
import { environmentaldatas } from "../../../../constant/defaultValues";
import { fetchPinData, useLoadPins } from "../../../../components/map/components/hooks/useLoadPins";
import { PinCountApi, GetFloorData } from "../../../../components/map/components/helpers/projectApi";
import { useSelector } from "react-redux";

const ProjectHeaderContext = createContext(null);

export const ProjectHeaderProvider = ({ children }) => {
    const { id }        = useParams(); 
    const decodedId     = decode(id); 
    const navigate      = useNavigate();
    const dispatch      = useDispatch();
    const { image_url } = environmentaldatas;
    
    const currentFloor = useSelector((state) => state.api.currentFloor);
    const pinCount    = useSelector((state) => state.api.pinCount);
    
    const { loading : pinLoading, error, refetch  } = useLoadPins(decodedId);
    
    const [projectSettings, setProjectSettings] = useState({});
    const [projectSettingData, setProjectSettingData] = useState({});
    
    const [savingTimer, setSavingTimer] = useState(false);
    const [isPublish, setIsPublish] = useState("");
    const [isDiscard, setIsDiscard] = useState("");
    const [isDirty, setIsDirty] = useState(false);
    const [reset, setReset] = useState(false);

    const [itemMoved, setItemMoved] = useState(false);
    
    const [modal, setModal] = useState(false);
    const [showpassfield, setshowpassfield] = useState(false);

    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeModalFromEdit, setUpgradeModalFromEdit] = useState(false);
    const [publishPlanTier, setPublishPlanTier] = useState({ is_basic: 0, is_additional: 0, additional_count: 0 });
    const [isPublishing, setIsPublishing] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);
    
    const toggle = (type) => {
        if (type === "passprotect") {
            setshowpassfield(true);
        } else {
            setshowpassfield(false);
        }
        setModal((prev) => !prev);
    };
    
    const getProjectById = useCallback(async () => {
        try {
            const response = await getRequest(`project/${decodedId}`);
            const data = response.data ?? [];
            const newValue = data?.is_pass_protected
            ? { password: 111111, confirm_password: 111111 }
            : {};
            
            let center = JSON.parse(data?.center)
            const value = {
                ...data,
                ...newValue,
                width:  data?.width  ? Number(data.width)  : null,
                height: data?.height ? Number(data.height) : null,
                logo:   data?.logo   ? image_url + data?.logo : "",
                
                positions : center ? {
                    x: center?.lng,
                    y: center?.lat
                } : null,
                location_radius:  data?.radius_km ?? null,
                location_address: data?.address ?? null,
            }; 
            
            dispatch(setProjectData(value));
            
            if (data?.positions?.x != null && data?.positions?.y != null) {
                dispatch(setMapCenter([data.positions.x, data.positions.y]));
            }
            
            setProjectSettingData(value);
            setProjectSettings(value);
        } catch (error) {
            console.log(error);
        }
    }, [decodedId]);
    
    const getFloorList = useCallback(async () => {
        try {
            const response = await getRequest(`list-floor-plan/${decodedId}`);
            const data = response.data ?? [];  
            dispatch(setFloorList(data));
            dispatch(setCurrentFloor(data[0]));
        } catch (error) {
            console.log(error);
        }
    }, [decodedId]);
    
    const handleEnableDisable = useCallback(() => {
        EnableDisable(decodedId, setIsPublish, setIsDiscard);
    }, [decodedId]);
    
    const handleTotalPinCount = useCallback( async () => {  
        let pinCount = await PinCountApi(decodedId) 
        dispatch(setPinCount(pinCount)); 
    }, [decodedId]);
    
    const fetchFloorData = useCallback( async () => {  
        
        if(!currentFloor) return
        
        let getFloorData = await GetFloorData(currentFloor.enc_id)  
        let verticalItem = (getFloorData?.vertical_transports || []).map((item) => ({
            ...item,
            category:"vertical_transport",
            title:item?.vt_name ?? ''
        }))
        
        dispatch(
            setPinsByCategory({
                vertical_transport : verticalItem
            }
        ));
    }, [currentFloor]);
    
    const handleDiscard = async () => {
        try {
            setIsDiscarding(true);
            const reqUrl = `discard`;
            const data = { id: Number(decodedId) };
            const response = await postRequest(reqUrl, data);
            const result = response?.response?.data ?? [];
            toast.success(result?.message);
            // window.location.reload()
            await resetProjectState()
        } catch (error) {
            console.log(error);
            toast.error('Discard failed. Please try again.');
        } finally {
            setIsDiscarding(false);
        }
    };
    
    const onDiscardClick = () => {
        discardClick(handleDiscard);
    };
    

    const computePlanTier = (plan, pinCountData) => {
        const locationPinsUsed = pinCountData?.used_locations ?? 0; 
        const productPinsUsed  = pinCountData?.used_products  ?? 0; 

        const freeLoc   = Number(plan?.free_loc                 ?? 1);
        const freeProd  = Number(plan?.free_prod                ?? 200);
        const basicLoc  = Number(plan?.basic_location_pins      ?? 0);
        const basicProd = Number(plan?.basic_product_pins       ?? 0);
        const addLoc    = Number(plan?.additional_location_pins ?? 1);
        const addProd   = Number(plan?.additional_product_pins  ?? 1); 
        let is_basic = 0, is_additional = 0, additional_count = 0; 

        if (locationPinsUsed > freeLoc || productPinsUsed > freeProd) {
            is_basic = 1;
            const overflowLoc  = locationPinsUsed - freeLoc - basicLoc;
            const overflowProd = productPinsUsed  - freeProd - basicProd;
            if (overflowLoc > 0 || overflowProd > 0) {
                is_additional = 1;
                additional_count = Math.max(
                    overflowLoc  > 0 ? Math.ceil(overflowLoc  / addLoc)  : 0,
                    overflowProd > 0 ? Math.ceil(overflowProd / addProd) : 0
                );
            }
        }
        return { is_basic, is_additional, additional_count };
    };

    const doPublish = async (tier) => { 
        try {
            setIsPublishing(true);
            const t = tier ?? publishPlanTier;
            const reqUrl = `publish`;
            const data = {
                id:               Number(decodedId),
                is_basic:         t?.is_basic         ?? 0,
                is_additional:    t?.is_additional    ?? 0,
                additional_count: t?.additional_count ?? 0,
                is_upgrade : true
            }; 
            // console.log({tier, data, publishPlanTier});
            // return
            const response = await postRequest(reqUrl, data);
            const result   = response?.response?.data ?? [];
            const updated = await fetchPinData(decodedId, ['beacon']);
            dispatch(setPinsByCategory({ beacon: updated?.beacon }));
            
            handleEnableDisable();
            getProjectById();
            handleTotalPinCount();
            toast.success(result?.message);
        } catch (error) {
            console.log(error);
            toast.error('Publish failed. Please try again.');
        } finally {
            setIsPublishing(false);
        }
    };

    
    const handleUpgradeAndPublish = async (packs, onOpenStripe, explicitIsBasic = null) => {
        const planData      = showUpgradeModal;
        const isPaid        = Number(planData?.plan?.is_paid ?? 0);
        const tier          = publishPlanTier;
        const is_additional = packs > 0 ? 1 : 0;
        const is_basic      = explicitIsBasic !== null ? explicitIsBasic : (tier?.is_basic ?? 0);
        const updatedTier   = { is_basic, is_additional, additional_count: packs };

        setPublishPlanTier(updatedTier);
        setShowUpgradeModal(false);

        if (isPaid === 1) { 
            await doPublish(updatedTier);
        } else { 
            onOpenStripe && onOpenStripe(planData, updatedTier);
        }
    }; 
    
    const chargeAndPublish = async (tier) => {
        try {
            setIsPublishing(true);

            const chargeResponse = await chargePlanApi({
                project_id:       Number(decodedId),
                is_basic:         tier?.is_basic         ?? 0,
                is_additional:    tier?.is_additional    ?? 0,
                additional_count: tier?.additional_count ?? 0,
            });

            const chargeData = chargeResponse?.response?.data ?? chargeResponse?.data ?? chargeResponse ?? {};
            // console.log({chargeData,chargeResponse},"chargeData,chargeResponse");

            if (chargeData.type == '2' || chargeData?.status === 'payment_failed') {
                toast.error(chargeData?.message ?? 'Payment failed. Please try again.');
                return;
            }

            await doPublish(tier);

        } catch (error) {
            console.log(error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsPublishing(false);
        }
    };


    const publishYesClick = async () => {
        try {
           
            const planData = await PlanExpiryDetails(decodedId, () => {});
            if (!planData) return;

            const plan        = planData?.plan ?? {};
            const freeExpired = Number(plan?.free_expired ?? 0);
            const isPaid = Number(plan?.is_paid ?? 0);

            
            const tier = computePlanTier(plan, pinCount);
            setPublishPlanTier(tier);

            if (freeExpired === 0) {
                await doPublish(tier);
            } else {
                const isAtLimit = (pinCount?.used_locations > pinCount?.total_locations) || (pinCount?.used_products > pinCount?.total_products);

                if(isAtLimit){
                    setUpgradeModalFromEdit(false);
                    setShowUpgradeModal(planData); 
                }else{  
                    await doPublish({
                       additional_count : plan.additional_count,
                       is_additional : plan.additional_count > 0 ? 1 : 0,
                       is_basic : 1
                    });
                }
            }
            
        } catch (error) {
            console.log(error);
        }
    };
    
    const onPublishClick = () => {
        publishClick(publishYesClick, projectSettings);
    };

    const openUpgradeModal = useCallback(async () => {
        try {
            const planData = await PlanExpiryDetails(decodedId, () => {});
            if (!planData) return;
            const tier = computePlanTier(planData?.plan ?? {}, pinCount);
            setPublishPlanTier(tier);
            setUpgradeModalFromEdit(true);
            setShowUpgradeModal(planData);
        } catch (error) {
            console.log(error);
        }
    }, [decodedId, pinCount]);
    
    const onExitClick = () => {
        if (isDirty) {
            swal({
                title: "Are you sure you want to exit?",
                text: "You currently have pending changes. If you exit they'll be saved as a draft.",
                icon: "warning",
                buttons: [
                    { text: "No",  value: "No",  visible: true, className: "btn-danger",  closeModal: true },
                    { text: "Yes", value: "Yes", visible: true, className: "btn-success", closeModal: true },
                ],
            }).then((value) => {
                if (value === "Yes") {
                    navigate(`/project-list`);
                    dispatch(setShowLocationPicker(false))
                }
            });
        } else {
            navigate(`/project-list`);
            dispatch(setShowLocationPicker(false))
        }
    };
    
    useEffect(() => {
        getProjectById();
        getFloorList()
        handleEnableDisable();
        handleTotalPinCount()
    }, [decodedId]);
    
    // useEffect(() => {
        //     fetchFloorData(); 
    // }, [currentFloor]);
    
    const resetProjectState = useCallback(async () => {
        try {
            setProjectSettings({});
            setProjectSettingData({});
            setSavingTimer(false);
            setIsPublish("");
            setIsDiscard("");
            setIsDirty(false);
            setModal(false);
            setshowpassfield(false);
            setReset(true)

            dispatch(setProjectData({}));
            dispatch(setFloorList([]));
            dispatch(setCurrentFloor(null));
            dispatch(setPinCount(0));
            dispatch(setPinsByCategory({}));
            dispatch(setMapCenter(null)); 
            dispatch(requestNavReload());

            await Promise.all([
                getProjectById(),
                getFloorList(),
                handleTotalPinCount(),
                refetch(),
            ]);

            handleEnableDisable();
            setReset(false)

        } catch (err) {
            console.error("Reset failed:", err);
        }
    }, [
        getProjectById,
        getFloorList,
        handleTotalPinCount,
        handleEnableDisable,
        dispatch
    ]);
    
    const value = { 
        projectSettings,
        setProjectSettings,
        projectSettingData,
        setProjectSettingData, 
        savingTimer,
        setSavingTimer,
        isPublish,
        isDiscard,
        isDirty,
        setIsDirty, 
        modal,
        toggle,
        showpassfield, 
        getProjectById,
        handleEnableDisable,
        onDiscardClick,
        onPublishClick,
        onExitClick,
        publishYesClick,
        reset,
        itemMoved, setItemMoved,
        showUpgradeModal,
        setShowUpgradeModal,
        publishPlanTier,
        isPublishing,
        isDiscarding,
        doPublish,
        handleUpgradeAndPublish,
        chargeAndPublish,
        openUpgradeModal,
        upgradeModalFromEdit,
    };
    
    return (
        <>
            <ProjectHeaderContext.Provider value={value}>
                {children}
            </ProjectHeaderContext.Provider>
        </>
    );
};

export const useProjectHeader = () => {
    const ctx = useContext(ProjectHeaderContext);
    if (!ctx) {
        throw new Error("useProjectHeader must be used inside <ProjectHeaderProvider>");
    }
    return ctx;
};
